import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import HelpAndSupportService from "../../services/http/services/HelpAndSupportService";
import SupportService from "../../services/http/services/SupportService";

export type HelpAndSupportTab = "usuarios" | "externo-app" | "externo-site";

export default function useHelpAndSupport() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const [counts, setCounts] = useState({
        appUsers: 0,
        appUsersUnread: 0,
        appUsersOpen: 0,
        externalApp: 0,
        externalAppOpen: 0,
        externalSite: 0,
        externalSiteOpen: 0,
    });

    const { activeCity, user } = useAuth();

    const canManageConfig = useMemo(() => {
        return user?.permissions?.some((p) => p.name === "manage_config") || false;
    }, [user]);

    const activeTab = useMemo<HelpAndSupportTab>(() => {
        const tab = searchParams.get("tab")?.toLowerCase();
        if (tab === "externo-app" || tab === "app") return "externo-app";
        if (tab === "externo-site" || tab === "site") return "externo-site";
        return "usuarios";
    }, [searchParams]);

    const handleChangeTab = useCallback(
        (key: string) => {
            setSearchParams({ tab: key });
        },
        [setSearchParams]
    );

    const loadCounts = useCallback(async (controller: AbortSignal) => {
        if (!activeCity?.id) return;
        try {
            const [contacts, externalApp, externalSite] = await Promise.all([
                HelpAndSupportService.listContacts(activeCity.id, controller).catch(() => []),
                SupportService.listSupportMessages({ cityId: activeCity.id, origin: "Aplicativo", signal: controller }).catch(() => []),
                canManageConfig
                    ? SupportService.listSupportMessages({ cityId: activeCity.id, origin: "Site", signal: controller }).catch(() => [])
                    : Promise.resolve([]),
            ]);

            const appUsersUnread = contacts.filter((c) => !c.panel_user_viewed).length;
            const appUsersOpen = contacts.filter((c) => c.status === "OPEN" || (c.status as string) === "Aberto").length;
            const externalAppOpen = externalApp.filter((s) => s.status === "Aberto" || s.status === "OPEN").length;
            const externalSiteOpen = externalSite.filter((s) => s.status === "Aberto" || s.status === "OPEN").length;

            setCounts({
                appUsers: contacts.length,
                appUsersUnread,
                appUsersOpen,
                externalApp: externalApp.length,
                externalAppOpen,
                externalSite: externalSite.length,
                externalSiteOpen,
            });
        } catch {
            // Silently ignore count load errors
        }
    }, [activeCity?.id, canManageConfig]);

    const handleRefreshAll = useCallback(async () => {
        const controller = new AbortController();
        setIsRefreshing(true);
        setRefreshKey((prev) => prev + 1);
        await loadCounts(controller.signal);
        setIsRefreshing(false);
    }, [loadCounts]);

    useEffect(() => {
        if (!activeCity?.id) return;

        const controller = new AbortController();
        loadCounts(controller.signal);

        return () => {
            controller.abort();
        };
    }, [activeCity?.id, loadCounts, refreshKey]);

    const totalAllTickets = useMemo(() => {
        return counts.appUsers + counts.externalApp + (canManageConfig ? counts.externalSite : 0);
    }, [counts, canManageConfig]);

    return {
        activeTab,
        handleChangeTab,
        refreshKey,
        isRefreshing,
        handleRefreshAll,
        counts,
        totalAllTickets,
        canManageConfig,
    };
}
